import type {
  ProductAttribute,
  ProductAttributeUnion,
  ProductAttributeValueAmountUnit,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import type { AttributeDefinitionFragment } from '@hub/generated/graphql';
import type {
  TechnicalDataAttribute,
  TechnicalDataAttributes,
} from '@hub/bearing-hub/ProductDetails/AkeneoProductData/technical-data';
import type { UnitCode, UnitSystem } from '@icommerce/units';
import {
  AngleCode,
  getUnitLabel,
  getUnitLabelForUnitSystem,
  LengthCode,
  PressureCode,
  SpeedCode,
  TemperatureCode,
} from '@icommerce/units';
import { BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import { i18n } from 'next-i18next';
import type { FlatBevelData, IbhDimensions, Intersect } from '@hub/bearing-hub/common/types';
import { Locale } from '@formatjs/intl-locale';
import type { TechnicalData } from '@hub/models/data';
import type { TechnicalDataCategory } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

export type PartialTechnicalDataRecord = {
  name?: string;
  label?: string;
  attributes?: TechnicalDataAttributes;
  reference_entity_id?: string;
};

type TechnicalDataValues = ProductAttributeValueAmountUnit | string | boolean;

interface TechnicalDataWithValues {
  id: string;
  categoryName: string;
  description: string;
  value: TechnicalDataValues;
  icon?: string;
}

const imperialOnlyAttributes = ['d2_tolerance_housing_hole_min', 'd2_tolerance_housing_hole_max'];
const metricOnlyAttributes = [];

const filterOutUnitSystemSpecificAttributes = (attributeName: string, unitSystem: IbhUnitSystem) => {
  return unitSystem === IbhUnitSystem.Metric
    ? !imperialOnlyAttributes.includes(attributeName)
    : !metricOnlyAttributes.includes(attributeName);
};

const getTranslatedDescription = (source: string, key: string, locale: string) =>
  (i18n.getResource(new Locale(locale).language, source, key) as string) ||
  (i18n.getResource(new Locale('en').language, source, key) as string);

const getProperValueForUnitSystem = (values: ProductAttributeValueAmountUnit, unitSystem: IbhUnitSystem) => {
  if (typeof values === 'string' || typeof values === 'boolean') {
    return values;
  }

  const getDefaultTextToDisplay = (valuesToUse: ProductAttributeValueAmountUnit): string => {
    const defaultAmount = valuesToUse.amount;
    const defaultUnit = getUnitLabel(valuesToUse.unit);
    return `${defaultAmount} ${defaultUnit}`;
  };

  const getTextToDisplayFromCalculation = (valuesToUse: ProductAttributeValueAmountUnit): string => {
    const amountFromCalculation = Object.values(valuesToUse.calculations)[0];
    const unitFromCalculation = getUnitLabel(Object.keys(valuesToUse.calculations)[0] as UnitCode);
    return `${amountFromCalculation} ${unitFromCalculation}`;
  };

  if (!values.calculations || unitSystem === IbhUnitSystem.Metric) {
    return getDefaultTextToDisplay(values);
  }

  // When unit system is imperial use format: <imperial value> <imperial unit> (<metric value> <metric unit>)
  // Data fom Akeneo is inconsistent, so we need ensure that units are imperial
  // example of data  for given imperial unit system:
  // value { unit: 'INCH', amount: 0.5, calculations: { MILLIMETER: 12.7 } }
  // value { unit: 'CELSIUS', amount: 135, calculations: { FAHRENHEIT: 275 } }

  const expectedImperialUnits: UnitCode[] = [
    SpeedCode.FOOT_PER_SECOND,
    LengthCode.INCH,
    TemperatureCode.FAHRENHEIT,
    PressureCode.PSI,
  ];
  const unitIsImperial = expectedImperialUnits.includes(values.unit);
  const textToDisplayFromCalculation = getTextToDisplayFromCalculation(values);

  return `${unitIsImperial ? getDefaultTextToDisplay(values) : textToDisplayFromCalculation} (${
    unitIsImperial ? textToDisplayFromCalculation : getDefaultTextToDisplay(values)
  })`;
};

const sortTechnicalDataCategories = (itemA: TechnicalDataCategory, itemB: TechnicalDataCategory): number => {
  if (itemA.name?.indexOf('dimensions') === 0 && itemB.name?.indexOf('dimensions') !== 0) {
    return -1;
  }
  if (itemB.name?.indexOf('dimensions') === 0 && itemA.name?.indexOf('dimensions') !== 0) {
    return 1;
  }
  if (itemA.name?.indexOf('manufacturing_and_installation_tolerances') === 0) {
    return -1;
  }
  return 0;
};

const getToleranceSectionTo3DParts = (
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  dimensions: IbhDimensions,
  unitSystem: IbhUnitSystem,
  locale: string
): TechnicalData[] | [] => {
  if (!articleData.material.precision || articleData.productionMethod !== IbhProductionMethods.THREEDIMENSIONALPRINT) {
    return [];
  }

  const lengthUnit = getUnitLabelForUnitSystem(LengthCode.MILLIMETER, unitSystem as unknown as UnitSystem);
  const { precision } = articleData.material;
  const { d1 } = dimensions;
  const fractionDigits = unitSystem === IbhUnitSystem.Metric ? 2 : 4;

  return [
    {
      description: `d1 ${getTranslatedDescription('bearing-hub/bearingHub', 'COMMON.MIN', locale)}`,
      value: `${Number(d1 - precision).toFixed(fractionDigits)} ${lengthUnit}`,
    },
    {
      description: `d1 ${getTranslatedDescription('bearing-hub/bearingHub', 'COMMON.MAX', locale)}`,
      value: `${Number(d1 + precision).toFixed(fractionDigits)} ${lengthUnit}`,
    },
  ];
};

const getToleranceSectionForInjectionMoulding = (
  d1AfterPressFit: {
    min: number;
    max: number;
  },
  unitSystem: IbhUnitSystem,
  locale: string
): TechnicalData[] => {
  const translatedDescription = getTranslatedDescription(
    'bearing-hub/bearingHub',
    'TECHNICAL_DATA_LABELS.D1_AFTER_PRESS_FIT',
    locale
  );

  return [
    {
      description: `${translatedDescription} (max.)`,
      value: `${d1AfterPressFit.max} ${unitSystem === IbhUnitSystem.Metric ? 'mm' : 'in'}`,
    },
    {
      description: `${translatedDescription} (min.)`,
      value: `${d1AfterPressFit.min} ${unitSystem === IbhUnitSystem.Metric ? 'mm' : 'in'}`,
    },
  ];
};

const getDimensionsSection = (
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: IbhUnitSystem,
  dimensions: IbhDimensions
): TechnicalDataCategory => {
  const shapeKey = articleData.shape;
  const dimensionsToDisplay = dimensions || articleData.dimensions;
  const flatBevelData: FlatBevelData = {
    'f1.length': articleData?.bevels?.f1?.length,
    'f2.length': articleData?.bevels?.f2?.length,
    'f3.length': articleData?.bevels?.f3?.length,
    'f4.length': articleData?.bevels?.f4?.length,
    'f1.angle': articleData?.bevels?.f1?.angle,
    'f2.angle': articleData?.bevels?.f2?.angle,
    'f3.angle': articleData?.bevels?.f3?.angle,
    'f4.angle': articleData?.bevels?.f4?.angle,
  };
  const lengthUnit = getUnitLabelForUnitSystem(LengthCode.MILLIMETER, unitSystem as unknown as UnitSystem);
  const angleUnit = getUnitLabel(AngleCode.DEGREE);
  const dimensionsDescriptionMap = BearingHubHelper.getDimensionsDescriptionMap(shapeKey, i18n, locale);
  const bevelsDescriptionMap = BearingHubHelper.getBevelsDescriptionMap(i18n, locale);

  const createAttributes = (
    data: Record<string, number | undefined> | IbhDimensions,
    descriptionMap: Record<string, string>,
    unit: string
  ) =>
    Object.entries(data)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => ({
        description: descriptionMap[key],
        value: `${String(value)} ${key.includes('angle') ? angleUnit : unit}`,
      }));

  const dimensionsAttributes = createAttributes(dimensionsToDisplay, dimensionsDescriptionMap, lengthUnit);
  const bevelAttributes = createAttributes(flatBevelData, bevelsDescriptionMap, lengthUnit);

  return {
    name: 'dimensions',
    label: getTranslatedDescription('bearing-hub/bearingHub', 'HEADLINES.DIMENSIONS', locale),
    attributes: [...dimensionsAttributes, ...bevelAttributes],
  };
};

const getTechnicalDataWithValues = (
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  technicalData: PartialTechnicalDataRecord[],
  unitSystem: IbhUnitSystem
): TechnicalDataWithValues[] =>
  attributeDefinitions
    .map(attributeDefinition => {
      const attributeValue =
        productAttributes !== null ? productAttributes[attributeDefinition.attribute_id]?.value : undefined;
      const category: PartialTechnicalDataRecord | undefined = technicalData.find(record =>
        record.attributes.some(el => el.id === attributeDefinition.attribute_id)
      );
      const icon = (
        technicalData
          .find(el => el.name === category?.name)
          ?.attributes.find(attribute => attribute.id === attributeDefinition.attribute_id) as TechnicalDataAttribute
      )?.widenUrl;

      return {
        id: attributeDefinition.attribute_id,
        description: attributeDefinition.label,
        value:
          attributeValue && getProperValueForUnitSystem(attributeValue as ProductAttributeValueAmountUnit, unitSystem),
        categoryName: category?.name,
        icon: icon || '',
      };
    })
    .filter(item => item.value !== undefined);

const createTechnicalDataCategories = (
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  technicalData: PartialTechnicalDataRecord[],
  unitSystem: IbhUnitSystem
): TechnicalDataCategory[] =>
  technicalData.map(record => {
    const { name, label } = record;
    const categoryAttributes = getTechnicalDataWithValues(
      productAttributes,
      attributeDefinitions,
      technicalData,
      unitSystem
    )
      .filter(attribute => attribute.categoryName === name)
      .filter(attribute => filterOutUnitSystemSpecificAttributes(attribute.id, unitSystem));
    const attributes: TechnicalData[] = categoryAttributes.map(attribute => ({
      description: attribute.description,
      value: attribute.value as string | boolean,
      icon: attribute.icon,
    }));
    return {
      name,
      label,
      attributes,
    };
  });

const createTechnicalDataCategoriesWithFallbackData = (
  technicalDataRecords: TechnicalDataCategory[],
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: IbhUnitSystem,
  dimensions: IbhDimensions
): TechnicalDataCategory[] =>
  technicalDataRecords
    .map(record => {
      const newRecord = { ...record };
      const d1AfterPressFit = (articleData as IbhCustomArticleResponseDto)?.d1AfterPressFit;

      if (newRecord.name === 'dimensions') {
        return getDimensionsSection(articleData, locale, unitSystem, dimensions);
      }

      if (
        newRecord.name === 'manufacturing_and_installation_tolerances' &&
        articleData.productionMethod === IbhProductionMethods.THREEDIMENSIONALPRINT
      ) {
        newRecord.attributes = [
          ...newRecord.attributes,
          ...getToleranceSectionTo3DParts(articleData, dimensions, unitSystem, locale),
        ];
      } else if (newRecord.name === 'manufacturing_and_installation_tolerances' && d1AfterPressFit) {
        newRecord.attributes = [...getToleranceSectionForInjectionMoulding(d1AfterPressFit, unitSystem, locale)];
      }

      return newRecord;
    })
    .filter(record => record.attributes.length > 0);

export const getTechnicalDataCategories = (
  technicalData: PartialTechnicalDataRecord[],
  productAttributes: Record<string, ProductAttribute<Intersect<ProductAttributeUnion>>>,
  attributeDefinitions: AttributeDefinitionFragment[],
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto,
  locale: string,
  unitSystem: IbhUnitSystem,
  dimensions: IbhDimensions,
  isCustom: boolean
): TechnicalDataCategory[] => {
  const technicalDataCategories = createTechnicalDataCategories(
    productAttributes,
    attributeDefinitions,
    technicalData,
    unitSystem
  );
  if (isCustom) {
    const technicalDataCategoriesWithFallbackData = createTechnicalDataCategoriesWithFallbackData(
      technicalDataCategories,
      articleData,
      locale,
      unitSystem,
      dimensions
    );

    return technicalDataCategoriesWithFallbackData.sort(sortTechnicalDataCategories);
  }

  return technicalDataCategories.sort(sortTechnicalDataCategories);
};

export const getTechnicalDataCategoriesWithUnits = (
  technicalDataCategories: TechnicalDataCategory[],
  isCustom: boolean
) => {
  if (!isCustom) {
    return technicalDataCategories;
  }

  return technicalDataCategories.map(category => {
    if (category?.name && ['dimensions'].includes(category.name)) {
      return {
        ...category,
        attributes: category.attributes.map(el => ({
          ...el,
          value: `${el.value as string}`,
        })),
      };
    }
    return category;
  });
};
